import { ringLogger } from "./ringBufferLogger";
import { isAndroidPlatform } from "@/utils/platform";
import { openDB } from "idb";

export interface DiagnosticPayload {
  appName: string;
  product: string;
  appVersion: string;
  platform: "android" | "web";
  isOnline: boolean;
  userAgent: string;
  screen: {
    width: number;
    height: number;
    pixelRatio: number;
  };
  storageEstimate?: {
    usageMb: number;
    quotaMb: number;
    percentUsed: number;
  };
  database?: {
    activeDbName: string;
    version: number;
    manuscriptCount: number;
  };
  recentLogs: string;
  timestamp: string;
}

/**
 * Derives a clean, normalized error fingerprint for triage grouping
 */
export function computeErrorFingerprint(logs: string, category: string): string {
  if (!logs || logs === "(No logs recorded)") {
    return `${category.toUpperCase().replace(/\s+/g, "_")}_INQUIRY`;
  }

  // Prioritize well-known error signatures
  if (logs.includes("QuotaExceededError") || logs.includes("quota")) {
    return "IDB_QUOTA_EXCEEDED";
  }
  if (logs.includes("Failed to fetch") || logs.includes("NetworkError") || logs.includes("net::ERR_")) {
    return "NETWORK_FETCH_ERROR";
  }
  if (logs.includes("TransactionInactiveError") || logs.includes("IndexedDB")) {
    return "IDB_TRANSACTION_ERROR";
  }
  if (logs.includes("401") || logs.includes("Unauthorized") || logs.includes("token")) {
    return "AUTH_TOKEN_ERROR";
  }

  // Look for prominent error patterns in logs
  const errorMatch = logs.match(/\[ERROR\]\s*(\[[A-Za-z0-9_]+\])?\s*([A-Za-z0-9_]+Error|[A-Za-z0-9_\s]{5,40})/i);
  if (errorMatch && errorMatch[0]) {
    const raw = errorMatch[0].replace(/\[ERROR\]\s*/i, "").replace(/\[|\]/g, "").trim();
    return raw.replace(/[^a-zA-Z0-9_]/g, "_").slice(0, 32).toUpperCase();
  }

  return `${category.toUpperCase().replace(/\s+/g, "_")}_REPORT`;
}

/**
 * Collects runtime device & storage diagnostic details safely
 */
export async function collectDiagnostics(userId?: string | null): Promise<DiagnosticPayload> {
  const isAndroid = isAndroidPlatform();
  const activeDbName = userId ? `deckle-db-${userId}` : "deckle-db";

  // Storage estimation
  let storageEstimate: DiagnosticPayload["storageEstimate"] = undefined;
  if (typeof navigator !== "undefined" && navigator.storage?.estimate) {
    try {
      const estimate = await navigator.storage.estimate();
      if (estimate.usage !== undefined && estimate.quota !== undefined) {
        const usageMb = Math.round((estimate.usage / (1024 * 1024)) * 100) / 100;
        const quotaMb = Math.round((estimate.quota / (1024 * 1024)) * 100) / 100;
        const percentUsed = quotaMb > 0 ? Math.round((usageMb / quotaMb) * 100) : 0;
        storageEstimate = { usageMb, quotaMb, percentUsed };
      }
    } catch {
      // Storage estimate not supported or denied
    }
  }

  // Database inspection
  let databaseInfo: DiagnosticPayload["database"] = undefined;
  try {
    const db = await openDB(activeDbName, 3);
    let manuscriptCount = 0;
    if (db.objectStoreNames.contains("manuscripts")) {
      manuscriptCount = await db.count("manuscripts");
    }
    databaseInfo = {
      activeDbName,
      version: db.version,
      manuscriptCount,
    };
    db.close();
  } catch {
    // Database might not be initialized yet
  }

  return {
    appName: "Birch Fiction",
    product: "fiction",
    appVersion: "1.0.0",
    platform: isAndroid ? "android" : "web",
    isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "Unknown",
    screen: {
      width: typeof window !== "undefined" ? window.screen.width : 0,
      height: typeof window !== "undefined" ? window.screen.height : 0,
      pixelRatio: typeof window !== "undefined" ? window.devicePixelRatio : 1,
    },
    storageEstimate,
    database: databaseInfo,
    recentLogs: ringLogger.getFormattedLogs(),
    timestamp: new Date().toISOString(),
  };
}

/**
 * Format diagnostic payload into a clean, human-readable markdown block
 */
export function formatDiagnosticSummary(diagnostics: DiagnosticPayload): string {
  const { appName, product, platform, appVersion, isOnline, screen, storageEstimate, database, recentLogs, timestamp } = diagnostics;
  
  return [
    `=== ${appName?.toUpperCase() || "BIRCH FICTION"} DIAGNOSTIC REPORT ===`,
    `Product: ${appName || "Birch Fiction"} (${product || "fiction"})`,
    `Timestamp: ${timestamp}`,
    `Platform: ${platform.toUpperCase()} (v${appVersion}) | Online: ${isOnline ? "YES" : "NO"}`,
    `Display: ${screen.width}x${screen.height} @ ${screen.pixelRatio}x`,
    storageEstimate ? `Storage: ${storageEstimate.usageMb}MB used / ${storageEstimate.quotaMb}MB (${storageEstimate.percentUsed}%)` : `Storage: N/A`,
    database ? `Database: ${database.activeDbName} (v${database.version}, ${database.manuscriptCount} manuscripts)` : `Database: N/A`,
    `User-Agent: ${diagnostics.userAgent}`,
    ``,
    `=== RECENT APP LOGS ===`,
    recentLogs || "(No logs)",
  ].join("\n");
}
